interface ISpaceLoggerText {
  text: string
  isSecondary?: boolean
  addNewLine?: boolean
  addRocket?: boolean
}

/**
 * Generates space styled log messages with customizable options.
 * Each message can have a primary or secondary color, new lines can be added between messages,
 * and rocket emoji can be included. The styling includes a space-themed background.
 *
 * @param messages An array of objects representing log messages with optional formatting options.
 * @returns void
 *
 * @example
 * const messages: ISpaceLoggerText[] = [
 *  { text: 'hello ' },
 *  { text: 'world', isSecondary: true },
 *  { text: '!', addNewLine: true, addRocket: true },
 * ];
 * spaceLogger(messages);
 */
export function spaceLogger(messages: ISpaceLoggerText[]): void {
  const background = `
    background-image:
      repeating-linear-gradient(157.5deg, rgb(26,26,26) 0px,
        rgb(26,26,26) 10px, transparent 10px, transparent 11px),
      repeating-linear-gradient(67.5deg, rgb(26,26,26) 0px,
        rgb(26,26,26) 10px, transparent 10px, transparent 11px),
      linear-gradient(90deg, hsl(143,33%,50%), hsl(215,33%,50%),
        hsl(287,33%,50%), hsl(359,33%,50%), hsl(71,33%,50%));
  `

  let formattedLog = ""
  const logStyles: string[] = []

  messages.forEach((message, index) => {
    const { text, isSecondary, addNewLine, addRocket } = message

    const rocketEmoji = addRocket ? " \u{1F680}" : ""
    const textColor = isSecondary ? "#00D9B0" : "#FFFFFF"
    const fontWeight = isSecondary ? "" : "font-weight: bold;"
    let accumulatedStyles = "padding: 8px 0;"

    if (index === 0) {
      accumulatedStyles += "border-radius: 8px 0 0 0;"
    }

    if (addNewLine || index === messages.length - 1) {
      accumulatedStyles += "border-radius: 0 8px 8px 0;"
    }

    if (index === 0 || (index > 0 && messages[index - 1].addNewLine)) {
      accumulatedStyles += "padding-left: 8px;"
    }

    if (index === messages.length - 1 || addNewLine) {
      accumulatedStyles += "padding-right: 8px;"
    }

    formattedLog += `%c${text}${rocketEmoji}%c`
    logStyles.push(
      `${fontWeight} ${accumulatedStyles} color: ${textColor}; ${background}`,
      `${fontWeight} ${background}`,
    )

    if (addNewLine && index !== messages.length - 1) {
      formattedLog += "\n"
    }
  })

  console.log(formattedLog, ...logStyles)
}
